import type { WorkspaceFolder, WorkspaceProject } from "@/components/workspace/workspace-types";
import { useWorkspaceStore } from "@/hooks/use-workspace-data";
import Fuse from "fuse.js";
/**
 * Fuzzy workspace search.
 *
 * Indexes both folders and projects with Fuse.js so the workspace search bar
 * can match partial names ("rasp", "ada", "led") without a backend roundtrip.
 */
import { useMemo } from "react";

export interface WorkspaceSearchHit {
  kind: "folder" | "project";
  score: number;
  folder?: WorkspaceFolder;
  project?: WorkspaceProject;
}

interface IndexEntry {
  kind: "folder" | "project";
  folder?: WorkspaceFolder;
  project?: WorkspaceProject;
  haystack: string;
}

function buildIndex(folders: WorkspaceFolder[], projects: WorkspaceProject[]): IndexEntry[] {
  const out: IndexEntry[] = [];
  for (const f of folders) {
    out.push({
      kind: "folder",
      folder: f,
      haystack: `${f.name} folder`,
    });
  }
  for (const p of projects) {
    out.push({
      kind: "project",
      project: p,
      haystack: `${p.name} ${p.description ?? ""} ${p.branch ?? ""} ${p.default_branch ?? ""}`,
    });
  }
  return out;
}

export function useWorkspaceSearch(query: string, maxResults = 30): WorkspaceSearchHit[] {
  const folders = useWorkspaceStore((s) => s.folders);
  const projects = useWorkspaceStore((s) => s.projects);

  const fuse = useMemo(() => {
    const index = buildIndex(folders, projects);
    return new Fuse(index, {
      keys: ["haystack"],
      threshold: 0.4,
      ignoreLocation: true,
      includeScore: true,
    });
  }, [folders, projects]);

  return useMemo(() => {
    if (!query.trim()) return [];
    return fuse
      .search(query, { limit: maxResults })
      .map((r) => ({
        kind: r.item.kind,
        score: r.score ?? 1,
        folder: r.item.folder,
        project: r.item.project,
      }))
      .filter((hit) => hit.folder !== undefined || hit.project !== undefined);
  }, [fuse, query, maxResults]);
}
